// Components share semantic keys; their native objects remain the editable source.
const FLAVOUR = 'edusense:component';
const SCENE_FLAVOUR = 'edusense:scene';
const clone = value => JSON.parse(JSON.stringify(value));
const fields = ['x', 'y', 'width', 'height', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'textAlign', 'color', 'fill', 'stroke', 'strokeWidth'];
// A text's weight, style and alignment, and whether it keeps to one line (autoWidth: its width follows its words, as a
// printed line does) — given only when a component sets them, so scenes without them normalize as before.
const TEXT_STYLES = Object.freeze({ fontWeight: ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900'],
  fontStyle: ['normal', 'italic', 'oblique'], textAlign: ['left', 'center', 'right'] });
const specs = new WeakMap();
const conflict = () => new Error('연결된 구성 요소가 변경되거나 삭제되었습니다. 되돌리거나 연결을 해제한 뒤 다시 시도해 주세요.');

export function normalizeComponentScene(scene) {
  if (!scene || !Number.isFinite(scene.width) || !Number.isFinite(scene.height) || scene.width <= 0 || scene.height <= 0 || scene.width > 100000 || scene.height > 100000 || !Array.isArray(scene.elements) || scene.elements.length > 1000) throw new Error('구성 요소 장면이 올바르지 않습니다.');
  const keys = new Set();
  const elements = scene.elements.map(element => {
    if (!element || typeof element.key !== 'string' || !/^[\w.:-]{1,120}$/.test(element.key) || keys.has(element.key) || !['text', 'rect'].includes(element.type)) throw new Error('구성 요소의 종류 또는 의미 키가 올바르지 않습니다.');
    keys.add(element.key);
    for (const field of ['x', 'y', 'width', 'height']) if (!Number.isFinite(element[field]) || Math.abs(element[field]) > 100000 || (['width', 'height'].includes(field) && element[field] <= 0)) throw new Error('구성 요소의 위치 또는 크기가 올바르지 않습니다.');
    for (const field of ['color', 'fill', 'stroke']) if (element[field] !== undefined && (typeof element[field] !== 'string' || (typeof CSS !== 'undefined' ? !CSS.supports('color', element[field]) : !/^(#[0-9a-f]{3,8}|[a-z]+)$/i.test(element[field])))) throw new Error('구성 요소 색상이 올바르지 않습니다.');
    if (element.fontSize !== undefined && (!Number.isFinite(element.fontSize) || element.fontSize < 8 || element.fontSize > 200)) throw new Error('구성 요소 글자 크기가 올바르지 않습니다.');
    if (element.fontFamily !== undefined && (typeof element.fontFamily !== 'string' || !element.fontFamily || element.fontFamily.length > 120 || /[;{}<>]/.test(element.fontFamily))) throw new Error('구성 요소 글꼴이 올바르지 않습니다.');
    if (element.strokeWidth !== undefined && (!Number.isFinite(element.strokeWidth) || element.strokeWidth < 0 || element.strokeWidth > 50)) throw new Error('구성 요소 선 두께가 올바르지 않습니다.');
    if (element.type === 'text' && (typeof element.text !== 'string' || element.text.length > 20000)) throw new Error('구성 요소 글이 올바르지 않습니다.');
    if (element.dataProps !== undefined && (!Array.isArray(element.dataProps) || element.dataProps.some(field => !['x', 'y', 'width', 'height'].includes(field)))) throw new Error('구성 요소 데이터 속성이 올바르지 않습니다.');
    for (const [field, allowed] of Object.entries(TEXT_STYLES)) if (element[field] !== undefined && (element.type !== 'text' || !allowed.includes(String(element[field])))) throw new Error('구성 요소 글 서식이 올바르지 않습니다.');
    if (element.autoWidth !== undefined && (element.type !== 'text' || typeof element.autoWidth !== 'boolean')) throw new Error('구성 요소 글 자동 너비가 올바르지 않습니다.');
    return { key:element.key, type:element.type, ...Object.fromEntries(fields.filter(field => element[field] !== undefined).map(field => [field, field === 'fontWeight' ? String(element[field]) : element[field]])), ...(element.type === 'text' ? {text:element.text, fontSize:element.fontSize ?? 16, fontFamily:element.fontFamily ?? 'Arial', color:element.color ?? '#17243b', ...(element.autoWidth ? {autoWidth:true} : {})} : {fill:element.fill ?? 'transparent', stroke:element.stroke ?? 'transparent', strokeWidth:element.strokeWidth ?? 0}), dataProps:[...(element.dataProps || [])] };
  });
  return { width:scene.width, height:scene.height, elements };
}

export function mergeComponentElement(actual, baseline, incoming) {
  const next = clone(incoming);
  for (const field of fields) {
    if (actual[field] !== undefined && actual[field] !== baseline[field]) next[field] = actual[field];
  }
  return next;
}

/** Native schema + view registered in the same runtime as the document editor. */
export function componentBlockSpec(B) {
  if (specs.has(B)) return specs.get(B);
  const schema = B.defineBlockSchema({ flavour:FLAVOUR, props:() => ({instanceId:'', scene:{width:1000, height:640, elements:[]}}), metadata:{version:1, role:'content', children:[]} });
  const sceneSchema = B.defineBlockSchema({ flavour:SCENE_FLAVOUR, props:() => ({scene:{width:1000, height:640, elements:[]}}), metadata:{version:1, role:'content', children:[]} });
  class ComponentBlock extends B.BlockComponent {
    connectedCallback() { super.connectedCallback(); this.contentEditable = 'false'; }
    updated(changed) {
      super.updated(changed);
      // Editing replaces the browser's text nodes. Keep Lit's child markers out
      // of the editable region and avoid resetting the caret after every input.
      for (const element of this.model.scene.elements) {
        if (element.type !== 'text') continue;
        const node = this.querySelector(`[data-component-key="${element.key}"]`);
        if (node && node.textContent !== element.text) node.textContent = element.text;
      }
    }
    updateText(key, event) {
      if (this.doc.readonly) return;
      const scene = clone(this.model.scene), element = scene.elements.find(item => item.key === key);
      const text = event.currentTarget.innerText.replaceAll('\r\n', '\n');
      if (!element || text === element.text) return;
      if (text.length > 20000) { event.currentTarget.textContent = element.text; return; }
      element.text = text;
      this.doc.captureSync(); this.doc.updateBlock(this.model, {scene}); this.doc.captureSync();
    }
    renderBlock() {
      const scene = normalizeComponentScene(this.model.scene);
      return B.html`<div data-component-instance=${this.model.instanceId ?? ''} style="margin:16px 0;break-inside:avoid;line-height:1.25">
        <svg role="group" aria-label=${this.model.flavour === FLAVOUR ? '연결된 구성 요소' : '일반 편집 블록'} xmlns="http://www.w3.org/2000/svg" viewBox=${`0 0 ${scene.width} ${scene.height}`} style="display:block;width:100%;height:auto;overflow:visible">
          ${scene.elements.map(element => element.type === 'rect' ? B.svg`<rect data-component-key=${element.key} x=${element.x} y=${element.y} width=${element.width} height=${element.height} fill=${element.fill} stroke=${element.stroke} stroke-width=${element.strokeWidth}></rect>` : B.svg`<foreignObject x=${element.x} y=${element.y} width=${element.width} height=${element.height} overflow="visible">${B.html`<div xmlns="http://www.w3.org/1999/xhtml" data-component-key=${element.key} role="textbox" aria-label=${element.key} contenteditable=${this.doc.readonly ? 'false' : 'plaintext-only'} spellcheck="false" style=${`white-space:${element.autoWidth ? 'pre' : 'pre-wrap;overflow-wrap:anywhere'};min-height:100%;font-family:${element.fontFamily};font-size:${element.fontSize}px;${element.fontWeight ? `font-weight:${element.fontWeight};` : ''}${element.fontStyle ? `font-style:${element.fontStyle};` : ''}${element.textAlign ? `text-align:${element.textAlign};` : ''}color:${element.color};line-height:1.25;outline:none`} @pointerdown=${event => event.stopPropagation()} @keydown=${event => event.stopPropagation()} @input=${event => this.updateText(element.key, event)} @blur=${event => this.updateText(element.key, event)}></div>`}</foreignObject>`)}
        </svg>
      </div>`;
    }
  }
  if (!customElements.get('edusense-component-block')) customElements.define('edusense-component-block', ComponentBlock);
  if (!customElements.get('edusense-scene-block')) customElements.define('edusense-scene-block', class extends ComponentBlock {});
  const result = {schemas:[schema, sceneSchema], extensions:[B.BlockViewExtension(FLAVOUR, B.literal`edusense-component-block`), B.BlockViewExtension(SCENE_FLAVOUR, B.literal`edusense-scene-block`)]};
  specs.set(B, result);
  return result;
}

/**
 * The width a one-line text needs in the loaded browser font: PDF font metrics and browser glyph hinting differ, so a
 * line printed unwrapped must fit the browser's measure as well (never narrower than it was given).
 */
export function autoTextWidth(element) {
  const context = document.createElement('canvas').getContext('2d');
  context.font = `${element.fontStyle || 'normal'} ${element.fontWeight || '400'} ${element.fontSize || 16}px ${JSON.stringify(element.fontFamily || 'Arial')}`;
  return Math.max(element.width, ...element.text.split('\n').map(line => Math.ceil(context.measureText(line).width) + 1));
}
function nativeProps(element) {
  const props = {xywh:JSON.stringify([element.x, element.y, element.autoWidth ? autoTextWidth(element) : element.width, element.height])};
  return element.type === 'text'
    ? {...props, fontSize:element.fontSize, fontFamily:element.fontFamily, color:element.color, hasMaxWidth:element.autoWidth !== true,
      ...Object.fromEntries(Object.keys(TEXT_STYLES).filter(field => element[field] !== undefined).map(field => [field, element[field]]))}
    : {...props, shapeType:'rect', shapeStyle:'General', fillColor:element.fill, strokeColor:element.stroke, strokeWidth:element.strokeWidth, filled:element.fill !== 'transparent'};
}
function nativeElement(model, key) {
  const [x, y, width, height] = JSON.parse(model.xywh);
  return model.type === 'text'
    ? {key, type:'text', x,y,width,height, fontSize:model.fontSize, fontFamily:model.fontFamily, fontWeight:model.fontWeight, fontStyle:model.fontStyle, textAlign:model.textAlign, color:model.color, text:model.text.toString()}
    : {key, type:'rect', x,y,width,height, fill:model.fillColor, stroke:model.strokeColor, strokeWidth:model.strokeWidth};
}

function placedScene(scene, placement) {
  const {x, y, scale} = placement;
  if (![x,y,scale].every(Number.isFinite) || scale <= 0 || scale > 1 || Math.abs(x) > 100000 || Math.abs(y) > 100000) throw conflict();
  return {...scene, elements:scene.elements.map(element => ({...element,
    x:x + element.x * scale, y:y + element.y * scale, width:element.width * scale, height:element.height * scale,
    ...(element.type === 'text' ? {fontSize:element.fontSize * scale} : {strokeWidth:element.strokeWidth * scale}),
  }))};
}

export function createBlockSuiteComponents({B, doc, surface, note, indexes, editable, mode, fonts}) {
  const transact = operation => { doc.captureSync(); let result; doc.transact(() => { result = operation(); }); doc.captureSync(); return result; };
  const getComponents = () => clone(doc.root.edusenseComponents);
  const storeRecord = (instanceId, record) => {
    const records = getComponents().filter(item => item.id !== instanceId);
    if (record) records.push(clone(record));
    doc.updateBlock(doc.root, {edusenseComponents:records});
  };
  function insertionPlacement(scene) {
    const frames = doc.getBlocksByFlavour('affine:frame');
    if (frames.length !== 1) return {x:0,y:0,scale:1};
    const frame = frames[0].model, [x,y,width,height] = JSON.parse(frame.xywh);
    const padding = Math.min(24, width / 10, height / 10);
    return {frameId:frame.id, x:x + padding, y:y + padding, scale:Math.min(1,(width-padding*2)/scene.width,(height-padding*2)/scene.height)};
  }
  function deleteElements(ids) {
    const removed = new Set(ids);
    for (const {model} of doc.getBlocksByFlavour('affine:frame')) {
      if (model.childIds.some(id => removed.has(id))) doc.updateBlock(model, {childElementIds:Object.fromEntries(Object.entries(model.childElementIds).filter(([id]) => !removed.has(id)))});
    }
    for (const id of ids) surface.deleteElement(id);
  }
  function assertBinding(binding, instanceId = binding?.instanceId) {
    if (!binding || binding.version !== 1 || binding.mode !== mode() || binding.instanceId !== instanceId || !binding.elements || typeof binding.elements !== 'object') throw conflict();
    if (binding.mode === 'page') {
      const block = doc.getBlockById(binding.blockId);
      if (!block || block.flavour !== FLAVOUR || block.instanceId !== instanceId) throw conflict();
      return block;
    }
    const ids = new Set();
    for (const entry of Object.values(binding.elements)) {
      const model = surface.getElementById(entry.id);
      if (!model || ids.has(entry.id) || (entry.type === 'text' ? model.type !== 'text' : model.type !== 'shape' || model.shapeType !== 'rect')) throw conflict();
      ids.add(entry.id);
    }
    return null;
  }
  // Rollback restores only the transaction's original native document state. Callers
  // keep the UI busy until commit; async native edits are detected before undo.
  function result(binding) {
    let finished = false, concurrent = false;
    const changed = () => { concurrent = true; };
    doc.spaceDoc.on('update', changed);
    const commit = () => { if (finished) return; finished = true; doc.spaceDoc.off('update', changed); };
    return {binding, commit, rollback() { if (finished) return; commit(); if (concurrent) throw new Error('구성 요소 처리 뒤 새 편집이 있어 자동으로 되돌릴 수 없습니다.'); doc.undo(); }};
  }
  return {
    getComponents,
    // `placement` places a new canvas component ({frameId?, x, y, scale}); otherwise it fills the page's single board.
    async upsertComponent({instanceId, scene:input, binding, record, placement:requested} = {}) {
      editable();
      if (typeof instanceId !== 'string' || !/^[\w.:-]{1,160}$/.test(instanceId)) throw new Error('구성 요소 ID가 올바르지 않습니다.');
      if (!record || record.id !== instanceId || typeof record.type !== 'string' || !Number.isInteger(record.version) || record.version < 1 || !record.props || !record.snapshot || Object.hasOwn(record, 'binding')) throw new Error('구성 요소 저장 정보가 올바르지 않습니다.');
      record = clone(record);
      const source = normalizeComponentScene(input);
      const targetMode = mode();
      if (binding) assertBinding(binding, instanceId);
      if (requested !== undefined && (binding || targetMode !== 'edgeless' || !requested || typeof requested !== 'object'
        || Object.keys(requested).some(key => !['frameId', 'x', 'y', 'scale'].includes(key))
        || (requested.frameId !== undefined && doc.getBlockById(requested.frameId)?.flavour !== 'affine:frame'))) throw conflict();
      const placement = targetMode === 'edgeless' ? (binding ? binding.placement ?? {x:0,y:0,scale:1} : requested ? clone(requested) : insertionPlacement(source)) : null;
      const scene = placement ? placedScene(source, placement) : source;
      await fonts(scene.elements);
      editable();
      if (targetMode !== mode()) throw conflict();
      const block = binding ? assertBinding(binding, instanceId) : null;
      if (!binding && (getComponents().some(item => item.id === instanceId) || doc.getBlocksByFlavour(FLAVOUR).some(item => item.model.instanceId === instanceId))) throw conflict();
      const next = {version:1, instanceId, mode:mode(), elements:{}};
      if (placement) next.placement = clone(placement);
      if (mode() === 'page') {
        const old = new Map((block?.scene.elements || []).map(element => [element.key, element]));
        if (binding && Object.keys(binding.elements).some(key => !old.has(key))) throw conflict();
        const merged = {...scene, elements:scene.elements.map(element => {
          const previous = binding?.elements[element.key];
          if (previous && previous.type !== element.type) throw conflict();
          next.elements[element.key] = {id:element.key, type:element.type, baseline:clone(element)};
          return previous ? mergeComponentElement(old.get(element.key), previous.baseline, element) : element;
        })};
        transact(() => {
          if (block) { doc.updateBlock(block, {scene:merged}); next.blockId = block.id; }
          else next.blockId = doc.addBlock(FLAVOUR, {instanceId, scene:merged}, note().id);
          storeRecord(instanceId, {...record, binding:next});
        });
      } else {
        // Frame children are not represented by the root layer's last index,
        // and native layer observers do not advance during this transaction.
        // Reserve increasing indexes above every existing object before adding.
        let lastIndex = indexes().reduce((last, value) => last === null || value > last ? value : last, null);
        const nextIndex = () => (lastIndex = B.generateKeyBetweenV2(lastIndex, null));
        const plan = scene.elements.map(element => {
          const previous = binding?.elements[element.key];
          if (previous && previous.type !== element.type) throw conflict();
          const model = previous ? surface.getElementById(previous.id) : null;
          if (model?.type === 'text' && model.text.toString() !== element.text) {
            const formats = new Set(model.text.toDelta().map(part => JSON.stringify(Object.entries(part.attributes || {}).sort(([a], [b]) => a.localeCompare(b)))));
            if (formats.size > 1) throw new Error('구성 요소 글에 서로 다른 부분 서식이 있습니다. 연결을 해제하거나 글 서식을 통일한 뒤 다시 계산해 주세요.');
          }
          return {element, model, merged:previous ? mergeComponentElement(nativeElement(model, element.key), previous.baseline, element) : element};
        });
        transact(() => {
          const wanted = new Set(scene.elements.map(element => element.key));
          deleteElements(Object.entries(binding?.elements || {}).filter(([key]) => !wanted.has(key)).map(([,previous]) => previous.id));
          const added = [];
          for (const {element, model, merged} of plan) {
            const props = nativeProps(merged);
            let id = model?.id;
            if (element.type === 'text') {
              if (model && model.text.toString() !== element.text) {
                // Preserve a user's uniform rich-text emphasis while replacing data.
                const attributes = model.text.toDelta()[0]?.attributes;
                model.text.delete(0, model.text.length); model.text.insert(0, element.text, attributes);
              } else if (!model) { props.text = new B.Y.Text(element.text); }
            }
            if (model) surface.updateElement(id, props);
            else { id = surface.addElement({...props, type:element.type === 'text' ? 'text' : 'shape', index:nextIndex()}); added.push(surface.getElementById(id)); }
            next.elements[element.key] = {id, type:element.type, baseline:clone(element)};
          }
          if (placement.frameId && added.length) doc.getBlockById(placement.frameId)?.addChildren(added);
          storeRecord(instanceId, {...record, binding:next});
        });
      }
      return result(next);
    },
    removeComponent(binding) {
      editable(); const block = assertBinding(binding);
      transact(() => { if (block) doc.deleteBlock(block); else deleteElements(Object.values(binding.elements).map(entry => entry.id)); storeRecord(binding.instanceId, null); });
      return result(null);
    },
    detachComponent(binding) {
      editable();
      // Deleting an individual native object is a legitimate reason to detach.
      // Remaining canvas objects are left untouched even if a binding is stale.
      if (!binding || binding.mode !== mode() || binding.version !== 1) throw conflict();
      if (binding.mode === 'edgeless') { transact(() => storeRecord(binding.instanceId, null)); return result(null); }
      const block = doc.getBlockById(binding.blockId);
      if (!block) { transact(() => storeRecord(binding.instanceId, null)); return result(null); }
      assertBinding(binding);
      const parent = doc.getParent(block), at = parent.children.findIndex(child => child.id === block.id);
      transact(() => {
        doc.addBlock(SCENE_FLAVOUR, {scene:clone(block.scene)}, parent.id, at);
        doc.deleteBlock(block);
        storeRecord(binding.instanceId, null);
      });
      return result(null);
    },
  };
}
